import { ButtonBase, type ButtonBaseProps } from "@mui/material";

import { type TextVariant } from "../../../ui-theme";
import { mergeSxProps } from "../../lib/mergeSxProps";
import { CbhIcon } from "../CbhIcon";
import { Text } from "../Text";

export const TEXT_BUTTON_SIZES = ["small", "medium", "large"] as const;
export type TextButtonSize = (typeof TEXT_BUTTON_SIZES)[number];

export interface TextButtonProps extends ButtonBaseProps {
  size?: TextButtonSize;
  href?: string;
  underline?: boolean;
  withLeftArrow?: boolean;
  withRightArrow?: boolean;
  LinkComponent: React.ElementType;
}

function textPropsBySize(size: TextButtonSize): TextVariant {
  switch (size) {
    case "small": {
      return "caption";
    }

    case "large": {
      return "body1";
    }

    case "medium": {
      return "body2";
    }

    default: {
      return "body2";
    }
  }
}

const TEXT_BUTTON_ICON_SIZE = {
  small: "xSmall",
  medium: "small",
  large: "small",
} as const;

const TEXT_BUTTON_ICON_MARGIN = {
  small: 1,
  medium: 2,
  large: 3,
} as const;

/**
 * A button that renders like a text link.
 *
 * NOTE: Provide `href` to make the button render as an
 * anchor tag. It will support react-router redirects. This is implemented as a separate component
 * rather than a Button variant since it has distinct visual styling and sizing requirements that
 * would be difficult to match with the standard Button variants.
 */
export function TextButton(props: TextButtonProps) {
  const {
    size = "medium",
    disabled,
    sx,
    underline = true,
    withLeftArrow = false,
    withRightArrow = false,
    children,
    LinkComponent,
    ...restProps
  } = props;

  return (
    <ButtonBase
      {...restProps}
      disabled={disabled}
      LinkComponent={LinkComponent}
      sx={mergeSxProps(
        (theme) => ({
          color: disabled
            ? theme.palette.intent?.disabled.text
            : theme.palette.intent?.neutral.text,
          display: "inline-flex",
          alignItems: "center",
          fontWeight: theme.typography.fontWeightMedium,
          height: "1.5rem",
          width: "fit-content",
          textDecoration: underline ? "underline" : "none",
          textDecorationThickness: "1px",
          textUnderlineOffset: "3px",
          textDecorationColor: underline
            ? String(
                disabled ? theme.palette.intent?.disabled.border : theme.palette.border?.primary
              )
            : undefined,

          ...(underline && {
            "@media (hover: hover)": {
              "&:hover": {
                textDecorationColor: String(theme.palette.border?.primaryInvert),
              },
            },

            "&:active": {
              textDecorationColor: String(theme.palette.border?.primaryInvert),
            },
          }),
        }),
        sx
      )}
    >
      {withLeftArrow && (
        <CbhIcon
          type="chevron-left"
          size={TEXT_BUTTON_ICON_SIZE[size]}
          sx={{ marginRight: TEXT_BUTTON_ICON_MARGIN[size] }}
        />
      )}
      <Text variant={textPropsBySize(size)}>{children}</Text>
      {withRightArrow && (
        <CbhIcon
          type="chevron-right"
          size={TEXT_BUTTON_ICON_SIZE[size]}
          sx={{ marginLeft: TEXT_BUTTON_ICON_MARGIN[size] }}
        />
      )}
    </ButtonBase>
  );
}
